import type { CityContentMapCafeRO, CityContentMapPinRO } from '@routellers/types/dist/city/city-content-map.ro';
import {
    ESTABLISHMENT_WEEKDAYS,
    type EstablishmentHours,
} from '@routellers/types/dist/city-establishment/establishment-hours';
import type { MouseEvent, ReactNode, WheelEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { enumLabel } from '../i18n';

type RouteLink = {
    id: number;
    title: string;
    status: string;
};

type Props =
    | {
          kind: 'place';
          place: CityContentMapPinRO;
          routes: RouteLink[];
          onShowRoute: (id: number) => void;
      }
    | {
          kind: 'cafe';
          cafe: CityContentMapCafeRO;
      };

const stopMapEvent = (event: MouseEvent | WheelEvent) => {
    event.stopPropagation();
};

const externalHref = (value: string | null | undefined): string | null => {
    const raw = value?.trim();

    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;

    return `https://${raw}`;
};

const instagramHref = (value: string | null): string | null => {
    const raw = value?.trim();

    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;

    const handle = raw.replace(/^@/, '').replace(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\//i, '');

    return handle ? `https://instagram.com/${handle}` : null;
};

const hourLines = (
    hours: EstablishmentHours | null,
    dayLabel: (day: string) => string,
    closed: string,
): string[] => {
    if (!hours) return [];

    return ESTABLISHMENT_WEEKDAYS.flatMap((day) => {
        const slot = hours[day];

        if (!slot) return [];
        if (slot.closed) return [`${dayLabel(day)} · ${closed}`];
        if (slot.open && slot.close) return [`${dayLabel(day)} ${slot.open}–${slot.close}`];

        return [];
    });
};

const durationLabel = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;

    return `${minutes}:${String(rest).padStart(2, '0')}`;
};

const knownForLine = (value: string): string =>
    value.startsWith('(') ? value : `(${value})`;

const Chips = ({ items }: { items: string[] }) =>
    items.length ? (
        <div className='city-map-pin-chips'>
            {items.map((item) => (
                <span key={item}>{item}</span>
            ))}
        </div>
    ) : null;

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <section className='city-map-pin-section'>
        <h4>{title}</h4>
        {children}
    </section>
);

const Photos = ({ urls, credit }: { urls: string[]; credit: string | null }) => {
    if (!urls.length) return null;

    if (urls.length === 1) {
        return (
            <>
                <img className='city-map-pin-hero' src={urls[0]} alt='' />
                {credit ? <p className='city-map-pin-credit'>{credit}</p> : null}
            </>
        );
    }

    return (
        <>
            <div className='city-map-pin-photos'>
                {urls.map((url) => (
                    <img key={url} src={url} alt='' />
                ))}
            </div>
            {credit ? <p className='city-map-pin-credit'>{credit}</p> : null}
        </>
    );
};

const Audio = ({ url, seconds }: { url: string; seconds: number | null }) => (
    <div className='city-map-pin-audio'>
        {seconds ? <span>{durationLabel(seconds)}</span> : null}
        <audio controls preload='none' src={url}>
            <track kind='captions' />
        </audio>
    </div>
);

const PlaceCard = ({
    place,
    routes,
    onShowRoute,
}: {
    place: CityContentMapPinRO;
    routes: RouteLink[];
    onShowRoute: (id: number) => void;
}) => {
    const { t } = useTranslation();
    const cards = place.cards ?? [];
    const lookFor = place.lookFor ?? [];
    const photos = place.photos ?? [];
    const people = place.people ?? [];
    const dayLabel = (day: string) => enumLabel(t, 'enums.weekday', day);
    const hours = hourLines(place.hours ?? null, dayLabel, t('pages.cityDetail.map.pin.closed'));
    const from = place.visitDurationMin;
    const to = place.visitDurationMax;
    const minutes =
        from != null || to != null
            ? from != null && to != null && from !== to
                ? `${from}–${to}`
                : String(from ?? to)
            : null;
    const chips = [
        place.isFree ? t('pages.cityDetail.map.pin.free') : null,
        place.ticketRequired ? t('pages.cityDetail.map.pin.ticket') : null,
        place.bookingRequired ? t('pages.cityDetail.map.pin.booking') : null,
        place.familyFriendly ? t('pages.cityDetail.map.pin.family') : null,
        place.rainFriendly ? t('pages.cityDetail.map.pin.rain') : null,
        place.nightFriendly ? t('pages.cityDetail.map.pin.night') : null,
        minutes ? t('pages.cityDetail.map.pin.duration', { minutes }) : null,
    ].filter((item): item is string => Boolean(item));
    const facts = [
        place.hoursNote,
        place.priceNote,
        place.visitNote,
        ...hours,
    ].filter((item): item is string => Boolean(item?.trim()));

    return (
        <>
            <Photos urls={photos} credit={place.photoCredit ?? null} />
            <div className={photos.length ? 'city-map-pin-body' : 'city-map-pin-body is-bare'}>
                <h3>{place.title}</h3>
                <p className='city-map-pin-meta'>
                    {enumLabel(t, 'enums.attractionReviewStatus', place.reviewStatus)}
                    {place.district ? ` · ${place.district}` : ''}
                </p>
                {place.address ? <p className='city-map-pin-address'>{place.address}</p> : null}
                {place.hook ? <p className='city-map-pin-hook'>{place.hook}</p> : null}
                {place.knownFor ? (
                    <p className='city-map-pin-known'>{knownForLine(place.knownFor)}</p>
                ) : null}
                <Chips items={chips} />
                {facts.length ? (
                    <div className='city-map-pin-facts'>
                        {facts.map((line) => (
                            <p key={line}>{line}</p>
                        ))}
                    </div>
                ) : null}
                {cards.length ? (
                    <Section title={t('pages.cityDetail.map.pin.nutshell')}>
                        {cards.map((card) => (
                            <article key={`${card.title}-${card.body}`} className='city-map-pin-cardlet'>
                                {card.title ? (
                                    <strong>
                                        {card.emoji ? `${card.emoji} ` : ''}
                                        {card.title}
                                    </strong>
                                ) : null}
                                {card.body ? <p>{card.body}</p> : null}
                            </article>
                        ))}
                    </Section>
                ) : null}
                {place.funFact ? (
                    <Section title={t('pages.cityDetail.map.pin.funFact')}>
                        <article className='city-map-pin-cardlet'>
                            {place.funFact.title ? (
                                <strong>
                                    {place.funFact.emoji ? `${place.funFact.emoji} ` : ''}
                                    {place.funFact.title}
                                </strong>
                            ) : null}
                            {place.funFact.body ? <p>{place.funFact.body}</p> : null}
                        </article>
                    </Section>
                ) : null}
                {lookFor.length ? (
                    <Section title={t('pages.cityDetail.map.pin.lookFor')}>
                        <ul>
                            {lookFor.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </Section>
                ) : null}
                {place.guideTalk ? (
                    <Section title={t('pages.cityDetail.map.pin.story')}>
                        <p className='city-map-pin-story'>{place.guideTalk}</p>
                    </Section>
                ) : null}
                {place.guideAudioUrl ? (
                    <Audio url={place.guideAudioUrl} seconds={place.guideAudioDurationSec} />
                ) : null}
                {people.length ? (
                    <Section title={t('pages.cityDetail.map.pin.people')}>
                        <p>{people.join(', ')}</p>
                    </Section>
                ) : null}
                {routes.length ? (
                    <Section title={t('pages.cityDetail.map.pin.routes')}>
                        <div className='city-map-pin-routes'>
                            {routes.map((route) => (
                                <button
                                    key={route.id}
                                    type='button'
                                    onClick={() => onShowRoute(route.id)}
                                >
                                    {route.title}
                                    {route.status !== 'published'
                                        ? ` · ${enumLabel(t, 'enums.readyRouteStatus', route.status)}`
                                        : ''}
                                </button>
                            ))}
                        </div>
                    </Section>
                ) : null}
            </div>
        </>
    );
};

const CafeCard = ({ cafe }: { cafe: CityContentMapCafeRO }) => {
    const { t } = useTranslation();
    const tags = cafe.tags ?? [];
    const offers = cafe.offers ?? [];
    const photos = cafe.photos ?? [];
    const dayLabel = (day: string) => enumLabel(t, 'enums.weekday', day);
    const hours = hourLines(cafe.hours ?? null, dayLabel, t('pages.cityDetail.map.pin.closed'));
    const price =
        cafe.priceLevel && cafe.priceLevel >= 1 && cafe.priceLevel <= 4
            ? '$'.repeat(cafe.priceLevel)
            : null;
    const chips = [
        ...tags,
        price,
        cafe.kind === 'cluster' ? enumLabel(t, 'enums.establishmentKind', cafe.kind) : null,
        ...offers.map((offer) => enumLabel(t, 'enums.establishmentOffer', offer)),
        cafe.openedYear
            ? t('pages.cityDetail.map.pin.since', { year: cafe.openedYear })
            : null,
    ].filter((item): item is string => Boolean(item));
    const links = [
        cafe.phone
            ? { href: `tel:${cafe.phone.replace(/[^\d+]/g, '')}`, label: cafe.phone }
            : null,
        cafe.website
            ? { href: externalHref(cafe.website), label: cafe.website }
            : null,
        cafe.instagram
            ? { href: instagramHref(cafe.instagram), label: cafe.instagram }
            : null,
        cafe.whatsapp
            ? { href: externalHref(cafe.whatsapp), label: cafe.whatsapp }
            : null,
        cafe.facebook
            ? { href: externalHref(cafe.facebook), label: cafe.facebook }
            : null,
        cafe.line ? { href: externalHref(cafe.line), label: cafe.line } : null,
    ].filter((item): item is { href: string; label: string } => Boolean(item?.href && item.label));

    return (
        <>
            <Photos urls={photos} credit={null} />
            <div className={photos.length ? 'city-map-pin-body' : 'city-map-pin-body is-bare'}>
                {cafe.neighborhood ? <p className='city-map-pin-eyebrow'>{cafe.neighborhood}</p> : null}
                <h3>{cafe.title}</h3>
                <p className='city-map-pin-meta'>
                    {enumLabel(t, 'enums.establishmentStatus', cafe.status)}
                </p>
                <Chips items={chips} />
                {cafe.pitch ? <p className='city-map-pin-hook'>{cafe.pitch}</p> : null}
                {hours.length || cafe.hoursNote ? (
                    <div className='city-map-pin-facts'>
                        {hours.map((line) => (
                            <p key={line}>{line}</p>
                        ))}
                        {cafe.hoursNote ? <p>{cafe.hoursNote}</p> : null}
                    </div>
                ) : null}
                {cafe.findHint ? (
                    <Section title={t('pages.cityDetail.map.pin.find')}>
                        <p>{cafe.findHint}</p>
                    </Section>
                ) : null}
                {cafe.address ? <p className='city-map-pin-address'>{cafe.address}</p> : null}
                {links.length ? (
                    <div className='city-map-pin-links'>
                        {links.map((link) => (
                            <a key={link.href} href={link.href} target='_blank' rel='noreferrer'>
                                {link.label}
                            </a>
                        ))}
                    </div>
                ) : null}
            </div>
        </>
    );
};

/** Place or cafe card. Same facts as the app sheet, anchored to the pin. */
export const CityMapPinCard = (props: Props) => (
    <div
        className='city-map-pin-scroll'
        onWheel={stopMapEvent}
        onDoubleClick={stopMapEvent}
        onMouseDown={stopMapEvent}
    >
        {props.kind === 'place' ? (
            <PlaceCard place={props.place} routes={props.routes} onShowRoute={props.onShowRoute} />
        ) : (
            <CafeCard cafe={props.cafe} />
        )}
    </div>
);
